"use client";

import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { cn } from "@/lib/utils";

export interface DataTableUrlState {
  page: number;
  setPage: (p: number) => void;
  filters: ColumnFiltersState;
  setFilters: (f: ColumnFiltersState) => void;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
  title?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  pageSize?: number;
  showPagination?: boolean;
  enableColumnFilters?: boolean;
  enableGlobalFilter?: boolean;
  enableVirtual?: boolean;
  urlState?: DataTableUrlState;
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  loading = false,
  title,
  emptyTitle = "داده‌ای یافت نشد",
  emptyDescription = "هنوز هیچ آیتمی برای نمایش وجود ندارد.",
  emptyAction,
  pageSize = 10,
  showPagination = true,
  enableColumnFilters = false,
  enableGlobalFilter = false,
  enableVirtual = false,
  urlState,
  className,
}: DataTableProps<TData, TValue>) {
  const [localSorting, setLocalSorting] = React.useState<SortingState>([]);
  const [localFilters, setLocalFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [globalFilter, setGlobalFilter] = React.useState("");

  const sorting = urlState?.sorting ?? localSorting;
  const setSorting = urlState?.setSorting ?? setLocalSorting;
  const columnFilters = urlState?.filters ?? localFilters;
  const setColumnFilters = urlState?.setFilters ?? setLocalFilters;

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: { pagination: { pageSize } },
  });

  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 56,
    overscan: 10,
    enabled: enableVirtual,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0]?.start ?? 0 : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)
      : 0;

  React.useEffect(() => {
    if (!urlState) return;
    const current = table.getState().pagination.pageIndex;
    if (current + 1 !== urlState.page) {
      table.setPageIndex(Math.max(0, urlState.page - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState?.page]);

  React.useEffect(() => {
    if (!urlState) return;
    urlState.setPage(table.getState().pagination.pageIndex + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().pagination.pageIndex]);

  if (loading) {
    return (
      <div className={cn("bg-white dark:bg-[hsl(var(--card))] border border-gray-200 dark:border-[hsl(var(--border))] rounded-xl shadow-sm overflow-hidden transition-colors", className)}>
        {title && <div className="p-5 border-b border-gray-100 dark:border-[hsl(var(--border))]"><Skeleton className="h-6 w-32 dark:bg-[hsl(var(--muted))]" /></div>}
        <div className="bg-gray-50/50 dark:bg-[hsl(var(--muted))] h-12 border-b border-gray-100 dark:border-[hsl(var(--border))]" />
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-4 border-b border-gray-100 dark:border-[hsl(var(--border))] last:border-b-0">
            <Skeleton className="h-10 w-10 rounded-md shrink-0 dark:bg-[hsl(var(--muted))]" />
            <Skeleton className="h-4 flex-1 dark:bg-[hsl(var(--muted))]" />
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn("bg-white dark:bg-[hsl(var(--card))] border border-gray-200 dark:border-[hsl(var(--border))] rounded-xl shadow-sm overflow-hidden transition-colors", className)}>
        {title && <div className="p-5 border-b border-gray-100 dark:border-[hsl(var(--border))] text-lg font-semibold text-gray-800 dark:text-[hsl(var(--foreground))]">{title}</div>}
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      </div>
    );
  }

  const startRecord = table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1;
  const totalRecords = table.getFilteredRowModel().rows.length;
  const endRecord = Math.min(startRecord + table.getState().pagination.pageSize - 1, totalRecords);

  return (
    <div className={cn("bg-white dark:bg-[hsl(var(--card))] border border-gray-200 dark:border-[hsl(var(--border))] rounded-xl shadow-sm overflow-hidden flex flex-col transition-colors", className)}>
      
      {/* هدر کارت */}
      {(title || enableGlobalFilter) && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 border-b border-gray-100 dark:border-[hsl(var(--border))]">
          {title && <h2 className="text-lg font-semibold text-gray-800 dark:text-[hsl(var(--foreground))]">{title}</h2>}
          {enableGlobalFilter && (
            <div className="relative w-full sm:w-72">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-[hsl(var(--muted-foreground))] pointer-events-none" />
              <Input
                placeholder="جستجو در همه ستون‌ها..."
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="h-9 pr-9 pl-3 text-sm bg-white dark:bg-[hsl(var(--background))] border-gray-300 dark:border-[hsl(var(--border))] text-gray-800 dark:text-[hsl(var(--foreground))] placeholder:text-gray-400 dark:placeholder:text-[hsl(var(--muted-foreground))] rounded-md shadow-sm focus-visible:ring-1 focus-visible:ring-primary dark:focus-visible:border-primary"
              />
            </div>
          )}
        </div>
      )}

      <div ref={tableContainerRef} className="overflow-x-auto overflow-y-auto flex-1" style={enableVirtual ? { maxHeight: 600 } : undefined}>
        <Table>
          {/* هدر جدول */}
          <TableHeader className="bg-gray-50/50 dark:bg-[hsl(var(--muted))]">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent border-b border-gray-200 dark:border-[hsl(var(--border))]">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const canFilter = enableColumnFilters && header.column.getCanFilter();
                  const meta = header.column.columnDef.meta as any;
                  const filterPlaceholder = meta?.filterPlaceholder || `جستجو...`;

                  return (
                    <TableHead key={header.id} className="h-auto py-3 px-4 align-top">
                      <div className="flex flex-col gap-2">
                        <div
                          className={cn(
                            "flex items-center justify-start text-[12px] font-bold text-gray-500 dark:text-[hsl(var(--muted-foreground))] uppercase tracking-wider",
                            canSort && "cursor-pointer select-none hover:text-gray-800 dark:hover:text-[hsl(var(--foreground))] transition-colors"
                          )}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        >
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            <span className="mr-1">
                              {sorted === "asc" ? (
                                <ChevronUp className="h-3.5 w-3.5" />
                              ) : sorted === "desc" ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                              )}
                            </span>
                          )}
                        </div>

                        {/* فیلتر هر ستون */}
                        {canFilter && (
                          <div className="relative w-full mt-1">
                            <Input
                              type={meta?.filterType === "number" ? "number" : "text"}
                              value={(header.column.getFilterValue() as string) ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                header.column.setFilterValue(val === "" ? undefined : val);
                              }}
                              placeholder={filterPlaceholder}
                              className="h-8 text-xs px-3 bg-white dark:bg-[hsl(var(--background))] border border-gray-300 dark:border-[hsl(var(--border))] text-gray-800 dark:text-[hsl(var(--foreground))] placeholder:text-gray-400 dark:placeholder:text-[hsl(var(--muted-foreground))] rounded-md shadow-none focus-visible:ring-1 focus-visible:ring-primary font-normal"
                            />
                          </div>
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          {/* بدنه جدول */}
          <TableBody>
            {enableVirtual && paddingTop > 0 && (
              <tr><td colSpan={columns.length} style={{ height: paddingTop }} /></tr>
            )}
            {(enableVirtual ? virtualItems.map((vi) => rows[vi.index]) : rows).map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className="hover:bg-gray-50/80 dark:hover:bg-[hsl(var(--accent))] transition-colors border-b border-gray-100 dark:border-[hsl(var(--border))] last:border-0 data-[state=selected]:bg-primary/5 dark:data-[state=selected]:bg-primary/20"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="py-4 px-4 text-sm text-gray-700 dark:text-[hsl(var(--foreground))]">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {enableVirtual && paddingBottom > 0 && (
              <tr><td colSpan={columns.length} style={{ height: paddingBottom }} /></tr>
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* فوتر و Paginator */}
      {showPagination && (
        <div className="px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card))]">
          
          {/* تعداد ردیف در صفحه */}
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-[hsl(var(--muted-foreground))] order-2 sm:order-1">
            <span>تعداد در صفحه:</span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              className="bg-transparent border-none focus:ring-0 cursor-pointer font-medium text-gray-800 dark:text-[hsl(var(--foreground))] dark:[&>option]:bg-[hsl(var(--card))]"
            >
              {[10, 25, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
            <span className="mr-4">
              {startRecord.toLocaleString("fa-IR")}-
              {endRecord.toLocaleString("fa-IR")} از {totalRecords.toLocaleString("fa-IR")} رکورد
            </span>
          </div>

          {/* دکمه‌های جهت‌دار */}
          <div className="flex items-center gap-1 order-1 sm:order-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-[hsl(var(--accent))] disabled:opacity-30 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent transition-colors text-gray-600 dark:text-[hsl(var(--muted-foreground))]"
              aria-label="صفحه قبل"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-[hsl(var(--accent))] disabled:opacity-30 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent transition-colors text-gray-600 dark:text-[hsl(var(--muted-foreground))]"
              aria-label="صفحه بعد"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}